import axios, { AxiosError } from 'axios';
import {
  FETCH_OPERATIONS_PAST_DUE_PERIODS_FAILURE,
  FETCH_OPERATIONS_PAST_DUE_PERIODS_REQUEST,
  FETCH_OPERATIONS_PAST_DUE_PERIODS_SUCCESS
} from '../constants/operationsReportingToDo';
import { OdataComparator } from '@pec/aion-ui-odata/types/odataComparator';
import { OdataResponse } from '@pec/aion-ui-odata/types/odataResponse';
import { QueryBuilder } from '@pec/aion-ui-odata/builders/odataQueryBuilder';
import { RootState } from 'combineReducers';
import { sendError } from '@pec/aion-ui-core/sendError';
import { ThunkAction } from 'redux-thunk';

const { Equals, NotEquals } = OdataComparator;

enum ReportStatus {
  Submitted = 'Submitted'
}

const { Submitted } = ReportStatus;

export type OperationsPastDuePeriod = {
  clientId: string;
};

const fetchOperationsPastDuePeriodsRequest = () =>
  ({
    type: FETCH_OPERATIONS_PAST_DUE_PERIODS_REQUEST
  } as const);

const fetchOperationsPastDuePeriodsSuccess = (payload: OperationsPastDuePeriod[]) =>
  ({
    type: FETCH_OPERATIONS_PAST_DUE_PERIODS_SUCCESS,
    payload
  } as const);

const fetchOperationsPastDuePeriodsFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: FETCH_OPERATIONS_PAST_DUE_PERIODS_FAILURE,
    error
  } as const;
};

const shouldFetchOperationsPastDuePeriods = ({ operationsReportingToDo: { isFetching, periods } }: RootState) =>
  !isFetching && !periods;

const getParams = (organizationId: string) => {
  const odataParams = new QueryBuilder()
    .select(['clientId'])
    .filter(f => f.filterBy('contractorId', Equals, organizationId).filterBy('reportStatus', NotEquals, Submitted))
    .toQueryParam();

  return { ...odataParams, removeUnreleasedClients: true };
};

const fetchOperationsPastDuePeriods = (
  organizationId: string
): ThunkAction<void, RootState, null, Actions> => async dispatch => {
  try {
    dispatch(fetchOperationsPastDuePeriodsRequest());

    const response = await axios.get<OdataResponse<OperationsPastDuePeriod[]>>(
      '/api/v3.01/operationalContractorPeriods',
      { params: getParams(organizationId) }
    );

    dispatch(fetchOperationsPastDuePeriodsSuccess(response.data.value));
  } catch (error) {
    dispatch(fetchOperationsPastDuePeriodsFailure(error));
  }
};

export const fetchOperationsPastDuePeriodsIfNeeded = (
  organizationId: string
): ThunkAction<void, RootState, null, Actions> => (dispatch, getState) => {
  if (shouldFetchOperationsPastDuePeriods(getState())) {
    dispatch(fetchOperationsPastDuePeriods(organizationId));
  }
};

export type Actions =
  | ReturnType<typeof fetchOperationsPastDuePeriodsRequest>
  | ReturnType<typeof fetchOperationsPastDuePeriodsSuccess>
  | ReturnType<typeof fetchOperationsPastDuePeriodsFailure>;
